import { useAui, useAuiState } from "@assistant-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import {
    DownloadIcon,
    FileTextIcon,
    Loader2Icon,
    PanelLeftCloseIcon,
    PanelLeftOpenIcon,
    PencilIcon,
    SheetIcon,
    UsersIcon,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router";
import { toast } from "sonner";

import {
    EditConversationDialog,
    type EditConversationUpdates,
} from "@/components/assistant-ui/EditConversationDialog";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import {
    chatTableDataToArtifactTable,
    citationHandleResolver,
} from "@/components/assistant-ui/utils/chat-table-to-artifact";
import { ShareConversationButton } from "@/components/frontend/chat/ShareConversationPopover";
import { AgentBadge } from "@/components/shared/AgentBadge";
import { TeamVisibleBadge } from "@/components/shared/TeamVisibleBadge";
import { TruncatedTooltip } from "@/components/shared/TruncatedTooltip";
import { Badge } from "@/components/ui/badge";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAccessMode } from "@/hooks/useAccessMode";
import { useConversationAgent } from "@/hooks/useConversationAgent";
import { useConversationClient } from "@/hooks/useConversationClient";
import { useCachedConversationDetail } from "@/hooks/useConversationDetail";
import { useConversationTeamVisible } from "@/hooks/useConversationTeamVisible";
import { useGenerateArtifactMutation } from "@/hooks/useGenerateArtifactMutation";
import { trackPollyThreadExported } from "@/lib/analytics";
import { saveConversationClient } from "../utils/conversationQueries";

import { extractTablesFromMessages } from "../utils/extractTablesFromMessages";
import { threadToMarkdown } from "../utils/threadToMarkdown";

interface ChatHeaderProps {
    sidebarOpen: boolean;
    onToggleSidebar: () => void;
}

// Backend `ArtifactGenerateRequest.tables` cap. Slice client-side and warn
// rather than letting the request 422 with an unexplained error.
const MAX_TABLES = 25;

/** Conversation title → filename slug. Shared by the .md and .xlsx exports
 * so the two filenames can't drift. */
function titleToSlug(title: string | undefined): string {
    return (title || "chat-export")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
}

export function ChatHeader({ sidebarOpen, onToggleSidebar }: ChatHeaderProps) {
    const aui = useAui();
    const isEmpty = useAuiState((s) => s.thread.isEmpty);
    const isRunning = useAuiState((s) => s.thread.isRunning);
    const remoteId = useAuiState((s) => {
        const id = s.threads.mainThreadId;
        return s.threads.threadItems.find((t) => t.id === id)?.remoteId;
    });
    const runtimeTitle = useAuiState((s) => {
        const id = s.threads.mainThreadId;
        return s.threads.threadItems.find((t) => t.id === id)?.title ?? "";
    });
    const detail = useCachedConversationDetail(remoteId);
    const threadTitle = runtimeTitle || detail?.title || "";
    // Between the first send and the server title only the header shows
    // "New Chat"; the rename dialog reads `threadTitle`, so it never pre-fills.
    const displayTitle = threadTitle || (isEmpty ? "" : "New Chat");

    const accessMode = useAccessMode(remoteId);
    const canRename = accessMode === "owner";

    // Reads agent_name off the same conversation detail cache that
    // useAccessMode already warms — no extra network round-trip.
    const { agentName } = useConversationAgent(remoteId);
    const { client } = useConversationClient(remoteId);
    const isTeamVisible = useConversationTeamVisible(remoteId);

    const queryClient = useQueryClient();
    const { chatId } = useParams<{ chatId: string }>();
    const mutation = useGenerateArtifactMutation();

    // Key the table-extraction memo on the message *count* (a primitive,
    // so `useAuiState`'s `Object.is` snapshot check is provably stable),
    // not the messages array identity. Messages are read imperatively
    // inside the memo, so re-parsing only happens when the count changes,
    // the thread switches, or streaming stops, not on every render. The
    // `!isRunning` gate skips parsing entirely while streaming (the
    // dropdown is hidden then anyway).
    const messageCount = useAuiState((s) => s.thread.messages.length);
    // The component never unmounts when you switch conversations; only the
    // main thread swaps underneath it. `messageCount` alone can't tell two
    // conversations apart (two threads of equal length collide on the same
    // key), which would strand the memo on the previous conversation's
    // tables. Keying on the thread id too forces a recompute on every
    // switch, so the badge and disabled-state always track the open thread.
    const mainThreadId = useAuiState((s) => s.threads.mainThreadId);
    const tables = useMemo(
        () =>
            isRunning
                ? []
                : extractTablesFromMessages(aui.thread().getState().messages),
        // messageCount + mainThreadId are intentional recompute triggers,
        // not read in the body (messages come from the imperative getState
        // above).
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [aui, isRunning, messageCount, mainThreadId]
    );
    // Single source of truth for the All-Tables action's enabled state,
    // shared by the handler's re-entry guard and the menu item's `disabled`.
    const canExportTables = tables.length > 0 && !mutation.isPending;

    const showExport = !isEmpty && !isRunning;
    const [renameOpen, setRenameOpen] = useState(false);

    const handleDownloadMarkdown = useCallback(() => {
        const { messages } = aui.thread().getState();
        const title = aui.threads().item("main").getState().title;
        // Resolve opaque cite:// markers into chat deep-links so the exported
        // links open the cited page (matches the xlsx export's deep-link).
        const md = threadToMarkdown(messages, {
            chatId,
            appHost: window.location.origin,
        });
        const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const filename = `${titleToSlug(title)}.md`;
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        trackPollyThreadExported({
            conversation_id: remoteId != null ? Number(remoteId) : null,
            format: "markdown",
            message_count: messages.length,
        });
    }, [aui, chatId, remoteId]);

    const handleDownloadAllTables = useCallback(() => {
        // Guard re-entry while a workbook is already generating: the menu
        // closes on select but the trigger stays live, so without this a
        // second open+click would fire a duplicate request into the
        // rate-limited endpoint. The trigger spinner signals the in-flight
        // state; this enforces it.
        if (!canExportTables) return;
        // Slice + warn client-side: the backend would 422 past the cap with
        // a message the error toast can't explain. The count badge still
        // shows the true total; only the workbook is truncated.
        const selected = tables.slice(0, MAX_TABLES);
        if (tables.length > MAX_TABLES) {
            toast.warning(`Only the first ${MAX_TABLES} tables were included.`);
        }
        const title = aui.threads().item("main").getState().title;
        mutation.mutate({
            type: "xlsx",
            // Resolve each table's native cite:// handles against the citation
            // map of the message it came from (handles are per-turn).
            tables: selected.map(({ table, resolutions }) =>
                chatTableDataToArtifactTable(
                    table,
                    citationHandleResolver(resolutions)
                )
            ),
            title,
            filename: `${titleToSlug(title)}-all-tables.xlsx`,
            ...(chatId ? { chat_id: chatId } : {}),
        });
        trackPollyThreadExported({
            conversation_id: remoteId != null ? Number(remoteId) : null,
            format: "xlsx",
            message_count: messageCount,
            table_count: selected.length,
        });
    }, [
        aui,
        canExportTables,
        chatId,
        messageCount,
        mutation,
        remoteId,
        tables,
    ]);

    const handleSave = useCallback(
        async (updates: EditConversationUpdates) => {
            if (updates.title !== undefined) {
                await aui.threads().item("main").rename(updates.title);
            }
            if (updates.client_id !== undefined && remoteId != null) {
                await saveConversationClient(
                    queryClient,
                    remoteId,
                    updates.client_id
                );
            }
        },
        [aui, queryClient, remoteId]
    );

    return (
        <header className="bg-background after:from-background relative z-10 flex shrink-0 flex-col px-3 after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:h-2 after:bg-gradient-to-b after:to-transparent">
            <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2">
                <TooltipIconButton
                    tooltip="Toggle sidebar"
                    aria-expanded={sidebarOpen}
                    aria-controls="chat-sidebar"
                    onClick={onToggleSidebar}
                >
                    {sidebarOpen ? (
                        <PanelLeftCloseIcon />
                    ) : (
                        <PanelLeftOpenIcon />
                    )}
                </TooltipIconButton>

                <div className="flex h-12 min-w-0 items-center justify-center px-2">
                    {displayTitle ? (
                        canRename ? (
                            <button
                                type="button"
                                onClick={() => setRenameOpen(true)}
                                aria-label="Rename conversation"
                                className="group text-foreground hover:bg-muted/50 focus-visible:bg-muted/40 focus-visible:ring-ring/40 flex max-w-full min-w-0 cursor-pointer items-center gap-1.5 rounded-md border-0 bg-transparent px-2.5 py-1 text-[15px] font-semibold tracking-tight transition-colors duration-150 focus-visible:ring-2 focus-visible:outline-none"
                            >
                                <TruncatedTooltip
                                    text={displayTitle}
                                    className="min-w-0 flex-1"
                                />
                                <PencilIcon
                                    aria-hidden="true"
                                    className="text-muted-foreground/70 size-3.5 shrink-0 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100"
                                />
                            </button>
                        ) : (
                            <span className="text-foreground block max-w-full min-w-0 px-2.5 py-1 text-[15px] font-semibold tracking-tight">
                                <TruncatedTooltip text={displayTitle} />
                            </span>
                        )
                    ) : null}
                </div>

                <div className="flex items-center gap-1">
                    {remoteId != null ? (
                        <ShareConversationButton
                            conversationId={remoteId}
                            accessMode={accessMode}
                            isTeamVisible={isTeamVisible}
                        />
                    ) : null}
                    {/* Hidden (empty thread or mid-stream): `opacity-0` keeps
                        the layout slot, but `inert` removes the control from
                        the tab order and the a11y tree; without it the button
                        would stay focusable while invisible. */}
                    <div
                        className={showExport ? undefined : "opacity-0"}
                        inert={showExport ? undefined : true}
                    >
                        <DropdownMenu>
                            <Tooltip>
                                <TooltipTrigger
                                    render={
                                        <DropdownMenuTrigger
                                            aria-busy={
                                                mutation.isPending || undefined
                                            }
                                            className="aui-button-icon hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring/40 inline-flex size-6 cursor-pointer items-center justify-center rounded-md p-1 focus-visible:ring-2 focus-visible:outline-none"
                                        />
                                    }
                                >
                                    {mutation.isPending ? (
                                        <Loader2Icon className="size-4 animate-spin motion-reduce:animate-none" />
                                    ) : (
                                        <DownloadIcon className="size-4" />
                                    )}
                                    <span className="sr-only">
                                        {mutation.isPending
                                            ? "Preparing Excel"
                                            : "Export conversation"}
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">
                                    Export conversation
                                </TooltipContent>
                            </Tooltip>
                            <DropdownMenuContent
                                align="end"
                                sideOffset={4}
                                className="w-48"
                            >
                                <DropdownMenuItem
                                    onClick={handleDownloadMarkdown}
                                >
                                    <FileTextIcon />
                                    Markdown (.md)
                                </DropdownMenuItem>
                                {/* Disabled when the thread has no tables (or
                                    a workbook is already generating). The
                                    primitive's `opacity-50` carries the
                                    disabled signal; the count badge only
                                    appears when there's a positive total. */}
                                <DropdownMenuItem
                                    onClick={handleDownloadAllTables}
                                    disabled={!canExportTables}
                                >
                                    <SheetIcon />
                                    All Tables (.xlsx)
                                    {tables.length > 0 && (
                                        <Badge
                                            variant="secondary"
                                            className="ml-auto tabular-nums"
                                        >
                                            {tables.length}
                                        </Badge>
                                    )}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>

                {agentName || client || isTeamVisible ? (
                    <div className="col-start-2 -mt-1 flex flex-wrap justify-center gap-x-1.5 gap-y-1 pb-2">
                        {agentName ? <AgentBadge agent={agentName} /> : null}
                        {client ? (
                            <span
                                className="bg-secondary text-secondary-foreground inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium"
                                data-testid="client-chip"
                            >
                                <UsersIcon
                                    aria-hidden="true"
                                    className="size-3 shrink-0"
                                />
                                {client.name}
                            </span>
                        ) : null}
                        {isTeamVisible ? <TeamVisibleBadge /> : null}
                    </div>
                ) : null}
            </div>

            {renameOpen && (
                <EditConversationDialog
                    open
                    onClose={() => setRenameOpen(false)}
                    currentTitle={threadTitle}
                    currentClient={client ?? null}
                    onSave={handleSave}
                />
            )}
        </header>
    );
}
